import { Combobox } from "design-system/organisms/Combobox"
import { cn } from "design-system/utils"
import { isFilled } from "lopermut"
import { useCustomersQuery } from "modules/customers/hooks/useCustomersQuery"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CustomersResponseDataSchema, GetCustomersSchema } from "schema"
import { z } from "zod"

type Option =
  | (React.ComponentProps<typeof Combobox>["options"][number] & {
      data?: z.infer<typeof CustomersResponseDataSchema>[number]
    })
  | undefined

type CustomersFormSelectorProps = {
  onChange: (value: Option | undefined) => void
  placeholder: string
  emptyPlaceholder: string
  searchPlaceholder: string
  className?: string
  avoidDisplaySelected?: boolean
  displayPhone?: boolean
  displayEmail?: boolean
  filterBy?: z.input<typeof GetCustomersSchema>["filterBy"]
  showAll?: boolean
  allLabel?: string
}

const CustomersFormSelector = ({
  onChange,
  placeholder,
  emptyPlaceholder,
  searchPlaceholder,
  className,
  avoidDisplaySelected,
  displayPhone,
  displayEmail,
  filterBy,
  showAll,
  allLabel,
}: CustomersFormSelectorProps) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebouncedValue({
    value: searchQuery,
  })
  const [selectedValue, setSelectedValue] = useState<Option>(undefined)

  const { data: customers, isPending } = useCustomersQuery({
    query: {
      query: debouncedSearchQuery,
      page: 1,
      total: 50,
      sortBy: { field: "name", order: "asc" },
      filterBy: {
        ...filterBy,
        isFake: false,
      },
    },
  })

  const options = useMemo(() => {
    const array: NonNullable<Option>[] =
      showAll && allLabel ? [{ label: allLabel, value: "all", muted: true }] : []

    if (customers) {
      array.push(
        ...customers.map((customers) => {
          const label = []

          if (displayPhone && isFilled(customers.phone)) {
            label.push(customers.phone)
          }
          if (displayEmail && isFilled(customers.email)) {
            label.push(customers.email)
          }

          const name = isFilled(customers.name) ? customers.name : t("customers.profile.noName")

          return {
            label: `${name}${label.length > 0 ? `: ${label.join(" - ")}` : ""}`,
            value: customers.id,
            data: customers,
          }
        }),
      )
    }

    return array
  }, [showAll, allLabel, customers, displayPhone, displayEmail, t])

  function onComboboxChange(option: Option) {
    const value = option?.value === "all" ? undefined : option

    if (!avoidDisplaySelected) {
      setSelectedValue(value)
    }
    onChange(value)
  }

  return (
    <div className={cn("flex items-center space-x-4", className)}>
      <Combobox
        options={options}
        value={
          selectedValue ??
          (showAll && allLabel ? { label: allLabel ?? "", value: "all" } : undefined)
        }
        onChange={onComboboxChange}
        placeholder={placeholder}
        emptyPlaceholder={emptyPlaceholder}
        searchPlaceholder={searchPlaceholder}
        disabled={isPending}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        className="w-full"
      />
    </div>
  )
}

export default CustomersFormSelector
